import React, { useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { AuthProvider } from './context/AuthContext';
import { ProductProvider } from './context/ProductContext';
import { ContentProvider, useContent } from './context/ContentContext'; // make sure useContent is exported
import ThemeProvider from './components/ThemeProvider';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import About from './pages/About';
import Contact from './pages/Contact';
import Sustainability from './pages/Sustainability';
import Returns from './pages/Returns';
import FAQ from './pages/FAQ';
import Shipping from './pages/Shipping';
import Wishlist from './pages/Wishlist';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import OrderConfirmation from './pages/OrderConfirmation';
import { fetchData } from './utils/api';
import ConfirmEmail from './pages/ConfirmEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

function AppContent() {
  const { content, loading } = useContent();

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  // Compute brand name & favicon URL once data is available
  const brandName = content?.settings?.brand_name || 'Madisa Farm';
  const faviconUrl = content?.settings?.brand_logo
    ? `${API_BASE}${content.settings.brand_logo.startsWith('/') ? '' : '/'}${content.settings.brand_logo}`
    : '/favicon.ico'; // fallback

  // Optional: log for debugging (remove later)
  useEffect(() => {
    if (!loading && content?.settings) {
      console.log('[App] Brand name loaded:', brandName);
      console.log('[App] Favicon URL:', faviconUrl);
    }
  }, [loading, content, brandName, faviconUrl]);

  return (
    <>
      <Helmet>
        <title>{brandName}</title>
        <link rel="icon" href={faviconUrl} />
        {/* Optional extras — good for mobile / PWA */}
        {content?.settings?.primary_color && (
          <meta name="theme-color" content={content.settings.primary_color} />
        )}
        {/* You can add apple-touch-icon, manifest, etc. later if needed */}
      </Helmet>

      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/shipping" element={<Shipping />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
}

function App() {
  // We keep a minimal fallback title until data loads
  const [initialBrandName] = React.useState('Madisa Farm');

  // You can keep your old fetch if you want — but ContentProvider already does this work
  // Keeping it commented out to avoid duplicate requests
  /*
  useEffect(() => {
    console.log('App.js: Providers mounted');
    const fetchBrandName = async () => {
      try {
        const data = await fetchData('/website/business-details', { noCache: true });
        if (data?.business?.name) {
          setBrandName(data.business.name);
        }
      } catch (err) {
        console.error('App: Failed to fetch business details:', err);
      }
    };
    fetchBrandName();
  }, []);
  */

  return (
    <HelmetProvider>
      <ThemeProvider>
        <CurrencyProvider>
          <AuthProvider>
            <CartProvider>
              <ProductProvider>
                <ContentProvider>
                  <Router>
                    <AppContent />
                  </Router>
                </ContentProvider>
              </ProductProvider>
            </CartProvider>
          </AuthProvider>
        </CurrencyProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;